import React, {
  useEffect, useMemo, useState,
} from 'react';
import {
  Modal, Row, Col,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import Form from 'components/Form/Layout';
import { yupCreateInvitation } from 'utils/yupFields';
import validateForm from 'utils/Validator';
import useSubmit from 'hooks/useSubmit';
import AdminInvitationsServices from 'services/api/AdminInvitations';
import { mailInvitationTemplate } from 'constants/mailInvitationTemplate';
import 'react-checkbox-tree/src/scss/react-checkbox-tree.scss';

import useDocumentTree from 'hooks/useDocumentTree';
import useValidateDocuments from 'hooks/useValidateDocuments';
import { splitInvitationDocumentSelection, storedFolderPathsToTreeValues } from 'utils/invitationDocuments';
import InvitationFormFields from 'screens/Admin/Invitations/components/InvitationFormFields';
import { mergeDateAndTimeUTC } from 'utils/date';
import SuccessMessage from '../SuccessMessage';

type CreateInvitationModalProps = {
  show: boolean,
  onClose: () => void,
  reloadData: () => void,
  initialData?: any;
};

function CreateInvitationModal( {
  show, onClose, reloadData, initialData,
}: CreateInvitationModalProps ) {
  const { t } = useTranslation();
  const [html, setHtml] = useState( mailInvitationTemplate );
  const [formSubmitted, setFormSubmitted] = useState( false );

  const {
    documentTree, expandedDocuments, setExpandedDocuments, futureInvestments, basePath,
  } = useDocumentTree();

  const {
    estado: validateEstado,
    progreso: validateProgreso,
    total: validateTotal,
    documentos: validateDocumentos,
    resumen: validateResumen,
    validacionId,
    errorMessage: validateErrorMessage,
    puedeEnviar,
    startValidation,
    resetValidation,
    translateDocStatus,
  } = useValidateDocuments();

  const validationSchema = yup.object( yupCreateInvitation() );
  const validate = validateForm( validationSchema );
  const initialValues = useMemo( () => {
    if ( initialData ) {
      let formattedDate = '';
      if ( initialData.fechaExpiracionEnlace ) {
        const dateObj = new Date( initialData.fechaExpiracionEnlace );
        const day = String( dateObj.getDate() ).padStart( 2, '0' );
        const month = String( dateObj.getMonth() + 1 ).padStart( 2, '0' );
        const year = dateObj.getFullYear();
        formattedDate = `${day}/${month}/${year}`;
      }

      return {
        type: initialData.type || 'PERSONAL',
        categoria: initialData.categoria || 'EMAIL',
        oportunidadId: initialData.oportunidadId,
        emails: initialData.emails || [],
        emailsCc: initialData.emailsCc || [],
        carpetasPaths: storedFolderPathsToTreeValues( initialData.carpetasPaths, basePath ),
        firmaAutomiticaNda: !!initialData.firmaAutomiticaNda,
        asuntoEmail: initialData.asuntoEmail || '',
        emailSender: initialData.emailSender || '',
        fechaExpiracionEnlace: formattedDate,
      };
    }

    return {
      type: 'PERSONAL',
      categoria: 'EMAIL',
      emails: [],
      emailsCc: [],
      carpetasPaths: [],
      firmaAutomiticaNda: false,
      validacionId: null,
    };
  }, [initialData, basePath] );

  // Sync the HTML template if cloning
  useEffect( () => {
    if ( initialData?.emailTemplate ) {
      setHtml( initialData.emailTemplate );
    }
  }, [initialData] );

  const submitForm = useSubmit( {
    promise: ( formData ) => AdminInvitationsServices.createAdminInvitationsDocuments( formData ),
    format: ( formData ) => {
      const { carpetasPaths, documentosIds } = splitInvitationDocumentSelection(
        formData.carpetasPaths,
        basePath,
      );

      const isPersonal = formData.type === 'PERSONAL';
      const isEnlace = isPersonal && formData.categoria === 'ENLACE';
      const firmaAutomiticaNda = isPersonal ? !!formData.firmaAutomiticaNda : false;
      const resolvedValidacionId = formData.validacionId ?? validacionId;
      const linkUuid = isEnlace && initialData?.linkUuid ? initialData.linkUuid : undefined;

      return {
        ...formData,
        carpetasPaths,
        documentosIds,
        firmaAutomiticaNda,
        validacionId: resolvedValidacionId,
        linkUuid,
        emailTemplate: formData?.type === 'PERSONAL' && formData?.categoria === 'EMAIL' ? html : null,
        fechaExpiracionEnlace: formData.fechaExpiracionEnlace
          ? mergeDateAndTimeUTC( formData.fechaExpiracionEnlace, '00:00:00' ) : null,
      };
    },
    callback: () => {
      setFormSubmitted( true );
      reloadData();
    },
    showAlert: false,
    deps: [html, basePath, validacionId, initialData],
  } );

  useEffect( () => {
    if ( !show ) {
      setFormSubmitted( false );
      setHtml( mailInvitationTemplate );
      resetValidation();
    }
  }, [show, resetValidation] );

  return (
    <Modal
      show={show}
      className="modal-carousel"
      centered
      fullscreen={formSubmitted ? 'false' : true}
      size={formSubmitted ? 'lg' : undefined}
      onHide={onClose}
    >
      <Modal.Header closeButton>
        <Row className="w-100">
          <Col>
            <h3 className="text-center">{t( 'admin.common.createInvitations' )}</h3>
          </Col>
        </Row>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={11} className="m-auto">
            {formSubmitted
              ? (
                <SuccessMessage
                  description={t( 'admin.common.createInvitationSuccess' )}
                  onClose={onClose}
                />
              )
              : (
                <Form
                  validate={validate}
                  onSubmit={submitForm}
                  initialValues={initialValues}
                  key={`${initialData?.id || 'form-new'}-${basePath}`}
                  body={( form ) => (
                    <InvitationFormFields
                      documentTree={documentTree}
                      futureInvestments={futureInvestments}
                      expandedDocuments={expandedDocuments}
                      setExpandedDocuments={setExpandedDocuments}
                      html={html}
                      onChangeEditor={( e ) => setHtml( e.target.value )}
                      onDocumentsSelectionChange={resetValidation}
                      validateDocuments={{
                        estado: validateEstado,
                        progreso: validateProgreso,
                        total: validateTotal,
                        documentos: validateDocumentos,
                        resumen: validateResumen,
                        errorMessage: validateErrorMessage,
                        puedeEnviar,
                        validacionId,
                        onValidate: () => {
                          const { values } = form.getState();
                          const payload = splitInvitationDocumentSelection(
                            values.carpetasPaths,
                            basePath,
                          );
                          startValidation( payload );
                        },
                        translateDocStatus,
                      }}
                    />
                  )}
                />
              )}
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default CreateInvitationModal;
